import React from 'react';

import { Text } from '@reckon-web/text';
import { Radio } from '@reckon-web/radio';
import { Stack } from '@reckon-web/stack';
import { Notice } from '@reckon-web/notice';

import {
  OutstandingTasksEnum,
  OutstandingTasksEnumValue,
} from '../components/work/types/outstanding-tasks-enum';
import {
  TaskStatusEnum,
  TaskStatusEnumValue,
} from '../components/work/types/task-status-enum';
import { Dialog } from '../components/common/dialog';
import {
  UpdateJobStatusInput,
  useUpdateJobStatus as _useUpdateJobStatus,
} from '../data/useUpdateJobStatus';
import { GetJobStatusListResult } from '../data/useGetJobStatusList';
import { JobStatusEnum } from '../components/job/types/job-status-enum';

import { useDialog } from './use-dialog';
import { JobSignOffStatus, JobSignOffStatusType } from './use-query-signoffs';

const CompleteOutstandingJobDialog: React.FC<{
  incompleteTasks: TransitionableJob['tasks'];
  onClose: (outstandingTasks?: OutstandingTasksEnumValue) => void;
}> = ({ incompleteTasks, onClose }) => {
  const [outstandingTasks, setOutstandingTasks] =
    React.useState<OutstandingTasksEnumValue>(
      OutstandingTasksEnum.valuesEnum.Completed
    );

  if (!incompleteTasks) {
    onClose();
    return null;
  }

  return (
    <Dialog
      title={
        incompleteTasks.length === 1
          ? 'Complete this job and mark remaining task?'
          : 'Complete this job and mark remaining tasks?'
      }
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, complete job"
      onSubmit={() => onClose(outstandingTasks)}
    >
      <Text>
        There {incompleteTasks.length === 1 ? 'is' : 'are'}{' '}
        <Text inline weight="semibold">
          {incompleteTasks.length}{' '}
          {incompleteTasks.length === 1 ? 'task' : 'tasks'}
        </Text>{' '}
        in this job that you haven't completed. What would you like to do with{' '}
        {incompleteTasks.length === 1 ? 'it' : 'them'}?
      </Text>
      <Stack
        background="muted"
        borderRadius="small"
        gap="small"
        padding="medium"
        role="radiogroup"
      >
        <Radio
          value={OutstandingTasksEnum.valuesEnum.Completed}
          checked={
            outstandingTasks === OutstandingTasksEnum.valuesEnum.Completed
          }
          onChange={() =>
            setOutstandingTasks(OutstandingTasksEnum.valuesEnum.Completed)
          }
        >
          <Text>
            Mark remaining {incompleteTasks.length === 1 ? 'task' : 'tasks'} as{' '}
            <Text inline leading="tighter" weight="semibold">
              Done
            </Text>
          </Text>
        </Radio>
        <Radio
          value={OutstandingTasksEnum.valuesEnum.Cancel}
          checked={outstandingTasks === OutstandingTasksEnum.valuesEnum.Cancel}
          onChange={() =>
            setOutstandingTasks(OutstandingTasksEnum.valuesEnum.Cancel)
          }
        >
          <Text>
            Mark remaining {incompleteTasks.length === 1 ? 'task' : 'tasks'} as{' '}
            <Text inline leading="tighter" weight="semibold">
              Cancelled
            </Text>
          </Text>
        </Radio>
        <Radio
          value={OutstandingTasksEnum.valuesEnum.Nothing}
          checked={outstandingTasks === OutstandingTasksEnum.valuesEnum.Nothing}
          onChange={() => {
            setOutstandingTasks(OutstandingTasksEnum.valuesEnum.Nothing);
          }}
        >
          <Text>Don't update tasks</Text>
        </Radio>
      </Stack>
    </Dialog>
  );
};

const CloseOutstandingJobDialog: React.FC<{
  incompleteTasks: TransitionableJob['tasks'];
  onClose: (outstandingTasks?: OutstandingTasksEnumValue) => void;
}> = ({ incompleteTasks, onClose }) => {
  const [outstandingTasks, setOutstandingTasks] =
    React.useState<OutstandingTasksEnumValue>();
  const [showError, setShowError] = React.useState(false);

  if (!incompleteTasks) {
    onClose();
    return null;
  }

  return (
    <Dialog
      title="Update incomplete tasks to close job"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, close job"
      onSubmit={() => {
        if (!outstandingTasks) {
          setShowError(true);
          return;
        }
        onClose(outstandingTasks);
      }}
    >
      <Text>
        There {incompleteTasks.length === 1 ? 'is' : 'are'}{' '}
        <Text inline weight="semibold">
          {incompleteTasks.length}{' '}
          {incompleteTasks.length === 1 ? 'task' : 'tasks'}
        </Text>{' '}
        in this job that you haven't completed. What would you like to do with
        them?
      </Text>
      <Stack
        background="muted"
        borderRadius="small"
        gap="small"
        padding="medium"
        role="radiogroup"
      >
        <Radio
          value={OutstandingTasksEnum.valuesEnum.Completed}
          checked={
            outstandingTasks === OutstandingTasksEnum.valuesEnum.Completed
          }
          onChange={() => {
            setOutstandingTasks(OutstandingTasksEnum.valuesEnum.Completed);
            setShowError(false);
          }}
        >
          <Text>
            Update remaining tasks to{' '}
            <Text inline leading="tighter" weight="semibold">
              Done
            </Text>{' '}
            and close this job
          </Text>
        </Radio>
        <Radio
          value={OutstandingTasksEnum.valuesEnum.Cancel}
          checked={outstandingTasks === OutstandingTasksEnum.valuesEnum.Cancel}
          onChange={() => {
            setOutstandingTasks(OutstandingTasksEnum.valuesEnum.Cancel);
            setShowError(false);
          }}
        >
          <Text>
            Update remaining tasks to{' '}
            <Text inline leading="tighter" weight="semibold">
              Cancelled
            </Text>{' '}
            and close this job
          </Text>
        </Radio>
        {showError && <Text color="critical">Required</Text>}
      </Stack>
      <Notice tone="cautious" size="small">
        Note: You will not be able to edit this job, or its tasks, once the job
        is closed.
      </Notice>
    </Dialog>
  );
};

const CloseJobDialog: React.FC<{
  onClose: (ok?: boolean) => void;
}> = ({ onClose }) => {
  return (
    <Dialog
      title="Close this job?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Close Job"
      onSubmit={() => onClose(true)}
    >
      <Text>
        You will not be able to edit this job, or its tasks, once the job is
        closed.
      </Text>
    </Dialog>
  );
};

const ReopenJobDialog: React.FC<{
  onClose: (ok?: boolean) => void;
}> = ({ onClose }) => {
  return (
    <Dialog
      title="Re-open job?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Re-open"
      onSubmit={() => onClose(true)}
    >
      <Text>Re-opening a job can impact billing and reporting.</Text>
    </Dialog>
  );
};

const CannotCloseJobDueToSignOffStatus: React.FC<{
  onClose: (ok?: boolean) => void;
}> = ({ onClose }) => {
  return (
    <Dialog
      title="Sign-off process is incomplete"
      submitLabel="OK"
      onSubmit={() => onClose(true)}
    >
      <Text>
        You can&rsquo;t close a job that has a sign-off request pending review.
        Once the sign-off is approved or withdrawn you will be able to close the
        job.
      </Text>
    </Dialog>
  );
};

const signoffStatusesToRejectJobClose = new Set([
  JobSignOffStatus.Requested,
  JobSignOffStatus.Declined,
  JobSignOffStatus.Revoked,
]);

// just the things we need to check when transitioning a job
export type TransitionableJob = {
  cloudId: string;
  status: GetJobStatusListResult;
  signOffStatus?: JobSignOffStatusType | null;
  tasks?: {
    status?: TaskStatusEnumValue;
  }[];
};

export const useUpdateJobStatus = () => {
  const [_updateJobStatus] = _useUpdateJobStatus(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const getParams = React.useCallback(
    async ({
      job,
      status,
    }: {
      job: any;
      status: any;
    }): Promise<UpdateJobStatusInput | undefined> => {
      const jobCloudId = job.cloudId || '';
      const jobSignOffStatus = job.signOffStatus;
      // prompt before reopening a job
      if (
        job.status.status === JobStatusEnum.valuesEnum.Closed &&
        status.status !== JobStatusEnum.valuesEnum.Closed
      ) {
        const ok = await openDialog<boolean>(ReopenJobDialog, {
          size: 'small',
        });
        if (!ok) return;
        return {
          cloudId: jobCloudId,
          jobStatusId: status.cloudId,
          outstandingTasks: OutstandingTasksEnum.valuesEnum.Nothing,
        };
      }

      // prevent moving to closed if the job is waiting for (or probably needs) signoff
      if (
        status.status === JobStatusEnum.valuesEnum.Closed &&
        job.signOffStatus &&
        signoffStatusesToRejectJobClose.has(jobSignOffStatus)
      ) {
        // there's not actually anything other than an OK on this one, but openDialog needs a return type
        await openDialog<boolean>(CannotCloseJobDueToSignOffStatus, {
          size: 'small',
        });
        return;
      }

      // incomplete task count is needed for the next couple of checks, so calculate it once
      const incompleteTaskStatuses = new Set<TaskStatusEnumValue>([
        TaskStatusEnum.valuesEnum.Done,
        TaskStatusEnum.valuesEnum.Cancelled,
      ]);
      const incompleteTasks = job.tasks?.filter(
        (task: { status: TaskStatusEnumValue }) =>
          task.status && !incompleteTaskStatuses.has(task.status)
      );

      // prompt if moving to completed and there are incomplete tasks
      if (
        status.status === JobStatusEnum.valuesEnum.Completed &&
        incompleteTasks &&
        incompleteTasks.length > 0
      ) {
        const outstandingTasks = await openDialog<OutstandingTasksEnumValue>(
          (props) => (
            <CompleteOutstandingJobDialog
              incompleteTasks={incompleteTasks}
              {...props}
            />
          ),
          { size: 'small' }
        );
        if (!outstandingTasks) return;
        return {
          cloudId: jobCloudId,
          jobStatusId: status.cloudId,
          outstandingTasks,
        };
      }

      // ask what to do with tasks for a closed job. This prompt is more important than the normal prompt to close
      if (
        status.status === JobStatusEnum.valuesEnum.Closed &&
        incompleteTasks &&
        incompleteTasks.length > 0
      ) {
        const outstandingTasks = await openDialog<OutstandingTasksEnumValue>(
          (props) => (
            <CloseOutstandingJobDialog
              incompleteTasks={incompleteTasks}
              {...props}
            />
          ),
          { size: 'small' }
        );
        if (!outstandingTasks) return;
        return {
          cloudId: jobCloudId,
          jobStatusId: status.cloudId,
          outstandingTasks,
        };
      }

      // prompt to close job
      if (status.status === JobStatusEnum.valuesEnum.Closed) {
        const ok = await openDialog<boolean>(CloseJobDialog, {
          size: 'small',
        });
        if (!ok) return;
        return {
          cloudId: jobCloudId,
          jobStatusId: status.cloudId,
        };
      }

      // all the way to here, nothing that needs prompting or stopping
      return {
        cloudId: jobCloudId,
        jobStatusId: status.cloudId,
      };
    },
    [openDialog]
  );

  const updateJobStatus = React.useCallback(
    async (params: Parameters<typeof getParams>[0]) => {
      const variables = await getParams(params);
      if (!variables) return;
      return _updateJobStatus({ variables: { input: variables } });
    },
    [_updateJobStatus, getParams]
  );

  return React.useMemo(
    () => [Dialog, updateJobStatus] as [typeof Dialog, typeof updateJobStatus],
    [Dialog, updateJobStatus]
  );
};
